/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Link } from '@/components/link';
import { DynamicImage } from '@/components/dynamic-image';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { formatCurrency } from '@/lib/currency';
import type { SearchPreviewProduct } from '@/lib/search/types';

interface SearchProductCardProps {
    product: SearchPreviewProduct;
    currency: string;
    onSelect: (product: SearchPreviewProduct) => void;
}

/**
 * Product card of the header search dropdown. Mobile renders a horizontal row (image, info, prices on the right);
 * from `md` up it becomes a 130px vertical card.
 */
export default function SearchProductCard({ product, currency, onSelect }: SearchProductCardProps): ReactElement {
    const { t, i18n } = useTranslation('search');
    const config = useConfig();
    const format = (price: number) => formatCurrency(price, i18n.language, currency);

    return (
        <Link
            to={product.url}
            onClick={() => onSelect(product)}
            data-testid="search-product-card"
            data-slot="search-dropdown-link"
            className="group flex w-full gap-2 bg-background md:w-[130px] md:flex-col md:gap-0">
            <div className="relative size-[72px] shrink-0 overflow-hidden rounded-lg border border-ps-disabled-stroke md:h-[120px] md:w-full md:border-0">
                {product.image ? (
                    <DynamicImage
                        src={`${toImageUrl({ src: product.image, config })}[?sw={width}]`}
                        alt={product.imageAlt || product.name}
                        widths={[72, 72, 130]}
                        imageProps={{ className: 'absolute inset-0 size-full object-contain' }}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
                        <ImageOff className="size-6" aria-hidden="true" />
                    </div>
                )}
            </div>

            <div className="flex min-w-0 flex-1 items-center gap-2 md:flex-col md:items-start md:p-2">
                <div className="min-w-0 flex-1 md:w-full">
                    {(product.brand || product.categoryName) && (
                        <div className="flex items-center gap-2 leading-6 md:flex-col md:items-start md:gap-0">
                            {product.brand && (
                                <span className="truncate text-sm font-semibold text-ps-text-label underline md:font-bold">
                                    {product.brand}
                                </span>
                            )}
                            {product.brand && product.categoryName && (
                                <span className="h-3.5 w-px shrink-0 bg-ps-gray-400 md:hidden" aria-hidden="true" />
                            )}
                            {product.categoryName && (
                                <span className="truncate text-xs text-ps-gray-500 md:text-sm">
                                    {product.categoryName}
                                </span>
                            )}
                        </div>
                    )}
                    <p className="line-clamp-1 text-xs font-medium leading-6 text-ps-text-label group-hover:underline md:text-sm md:font-normal">
                        {product.name}
                    </p>
                    <p className="break-all text-xs leading-6 text-ps-gray-500">
                        {t('dropdown.productId', { id: product.id })}
                    </p>
                </div>

                {(product.currentPrice !== undefined || product.lowStock) && (
                    <div className="flex shrink-0 flex-col items-end md:mt-1 md:w-full md:items-start">
                        {product.lowStock && (
                            <span
                                className="flex h-5 items-center rounded-[6px] bg-ps-accent px-2 text-[8px] font-medium leading-4 text-ps-white md:text-xs"
                                data-testid="search-product-low-stock">
                                {t('dropdown.lowStock')}
                            </span>
                        )}
                        {product.currentPrice !== undefined && (
                            <span className="text-sm font-semibold leading-6 text-ps-black md:text-base md:font-bold">
                                {format(product.currentPrice)}
                            </span>
                        )}
                        {product.currentPrice !== undefined && product.listPrice !== undefined && (
                            <span className="flex items-center gap-1 md:gap-2">
                                <span className="sr-only">{t('dropdown.listPrice')}</span>
                                <s className="text-[11px] font-medium leading-5 text-ps-disabled-text md:text-sm md:leading-6">
                                    {format(product.listPrice)}
                                </s>
                                {product.discountPercent !== undefined && (
                                    <span className="rounded-[6px] bg-ps-success-dark/16 px-[5px] py-0.5 text-[9px] font-medium leading-[14px] text-ps-success-dark md:text-xs md:leading-[18px]">
                                        -{product.discountPercent}%
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </Link>
    );
}
